import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import ProductCard from './ProductCard'
import ProductRail from './ProductRail'
import { SHOP_CATEGORIES, classifyCategory } from '../constants/shopCategories'
import { SITE_LINKS } from '../constants/siteLinks'
import './FeaturedProducts.css'

// Products in the "Popular Products" carousel
const MAX_PRODUCTS = 12

/** Home product → the shape the shared ProductCard (also used by the shop) expects. */
const toCardProduct = (product) => ({
  ...product,
  name: product.title,
  price: product.priceNewNum,
  priceOld: product.priceOldNum && product.priceOldNum > product.priceNewNum ? product.priceOldNum : null,
  category: product.badge,
})

/**
 * Home "Popular Products" section: a carousel of featured products (taken from each category in turn, so
 * the range is represented), using the shop's product cards.
 * Products use Home's display shape (title, image, priceNew/priceNewNum, priceOld/priceOldNum, badge);
 * `onOpen` gets that shape for the quick view, `onAdded` is told when a card adds to the cart.
 */
function FeaturedProducts({ products, loading, error, onOpen, onAdded }) {
  const [addedId, setAddedId] = useState(null)

  const handleAdded = (card) => {
    setAddedId(card.id)
    setTimeout(() => setAddedId(null), 2000)
    onAdded?.(card)
  }

  // Categories that have products, in catalogue order, each with its products
  const groups = useMemo(() => {
    const byCategory = new Map()
    products.forEach((product) => {
      const id = classifyCategory(product.badge).categoryId
      if (!byCategory.has(id)) byCategory.set(id, [])
      byCategory.get(id).push(product)
    })
    const order = SHOP_CATEGORIES.map((category) => category.id)
    return [...byCategory.entries()]
      .sort((a, b) => (order.indexOf(a[0]) + 1 || 99) - (order.indexOf(b[0]) + 1 || 99))
      .map(([id, items]) => ({ id, items, category: SHOP_CATEGORIES.find((c) => c.id === id) }))
  }, [products])

  // Round-robin across categories: one Starlink, one security, one power, then the next of each…
  const visible = useMemo(() => {
    const picked = []
    for (let round = 0; picked.length < MAX_PRODUCTS; round += 1) {
      const before = picked.length
      groups.forEach((group) => {
        if (group.items[round] && picked.length < MAX_PRODUCTS) picked.push(group.items[round])
      })
      if (picked.length === before) break
    }
    return picked
  }, [groups])

  const renderGrid = () => {
    if (loading) {
      return (
        <ul className="fp-grid" aria-busy="true" aria-label="Loading products">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i} className="fp-skel-card" aria-hidden="true">
              <span className="fp-skel fp-skel--media" />
              <span className="fp-skel" />
              <span className="fp-skel fp-skel--sm" />
              <span className="fp-skel fp-skel--btn" />
            </li>
          ))}
        </ul>
      )
    }

    if (error) {
      return (
        <div className="fp-state">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
          <p>We couldn't load products right now.</p>
          <button type="button" className="fp-btn fp-btn--primary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      )
    }

    if (visible.length === 0) {
      return (
        <div className="fp-state">
          <i className="fa-solid fa-box-open" aria-hidden="true" />
          <p>No products available at the moment.</p>
          <a href={SITE_LINKS.contact} className="fp-btn fp-btn--outline">
            Ask our team
          </a>
        </div>
      )
    }

    return (
      <ProductRail arrows="sides" title="Popular products">
        {visible.map((product) => (
          <li key={product.id}>
            <ProductCard
              product={toCardProduct(product)}
              addedProduct={addedId}
              onAdded={handleAdded}
              onQuickView={() => onOpen(product)}
            />
          </li>
        ))}
      </ProductRail>
    )
  }

  return (
    <section id="products" className="fp">
      <div className="fp__container">
        <header className="fp__head">
          <div>
            <span className="fab-chip">Featured products</span>
            <h2 className="fab-title">
              Popular <span className="fab-gradient-text">Products</span>
            </h2>
            <p className="fab-intro">High-quality, genuine products from trusted brands.</p>
          </div>
          <div className="fp__head-actions">
            <Link to={SITE_LINKS.shop} className="fp-btn fp-btn--outline">
              Browse Shop
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </Link>
          </div>
        </header>

        {renderGrid()}
      </div>
    </section>
  )
}

export default FeaturedProducts
